<script setup>
  import { ref, computed, provide, useTemplateRef, reactive, watchEffect, onErrorCaptured } from 'vue';
  import { useSpotStore } from '@/stores/SpotStore';
  import { useUserStore } from "@/stores/UserStore";
  import { useAlertStore } from '@/stores/AlertStore';
  import { useUserUpgrades } from '@/composables/useUserUpgrades';
  import { useDetailPanelStore } from '@/stores/DetailPanelStore';
  import { useBugHandler } from '@/composables/useBugHandler'; 
  import useUserHints from '@/composables/useUserHints';
  import useStorageSystem from '@/composables/useStorageSystem'
  import MapDetailPanelSpotHeader from '@/components/MapDetailPanelSpotHeader.vue';
  import WindGraphsContainer from '@/components/WindGraphsContainer.vue';
  import ForecastTableContainer from '@/components/ForecastTableContainer.vue';
  import ProForecastContainer from '@/components/ProForecastContainer.vue';
  import OnsiteReportsContent from '@/components/OnsiteReportsContent.vue';
  import AdvisoriesListing from '@/components/AdvisoriesListing.vue';
  import MarineForecastListing from '@/components/MarineForecastListing.vue';
  import NearbyTidesContent from '@/components/NearbyTidesContent.vue';
  import ArchiveDataContent from '@/components/ArchiveDataContent.vue';
  import StatsContent from '@/components/StatsContent.vue';
  import StationInfoContent from '@/components/StationInfoContent.vue';
  import GorgeWindbowContainer from '@/components/GorgeWindbowContainer.vue';
  import ControlButton from "@/components/controls/ControlButton.vue";
  import Popup from '@/components/controls/Popup.vue';
  import UserHint from '@/components/UserHint.vue';
  import ModalSaveContainerToProfile from '@/components/ModalSaveContainerToProfile.vue';
  import Icon from '@/components/icons/Icon.vue';
  import useContainerConfigurationGlobals from '@/composables/useContainerConfigurationGlobals';

  const { $WIND_GRAPH } = useContainerConfigurationGlobals();

  const containers = reactive({
    'wind-graph': useTemplateRef('wind-graph-container'),
    'forecast-table': useTemplateRef('forecast-table-container'),
    'pro-forecast': useTemplateRef('pro-forecast-container'),
    'onsite-report': useTemplateRef('onsite-report-container'),
    'advisories': useTemplateRef('advisories-container'),
    'marine-forecast': useTemplateRef('marine-forecast-container'),
    'nearby-tides': useTemplateRef('nearby-tides-container'),
    'archive-data': useTemplateRef('archive-data-container'),
    'spot-stats': useTemplateRef('stats-container'),
    'gorge-windbow': useTemplateRef('gorge-windbow-container'),
  });

  const detailPanel = useTemplateRef('detail-panel');
  const windGraphsSidebar = useTemplateRef('wind-graphs-sidebar');
  const windGraphsToolbar = useTemplateRef('wind-graphs-toolbar');

  const spotStore = useSpotStore();
  const userStore = useUserStore();
  const alertStore = useAlertStore();
  const detailPanelStore = useDetailPanelStore();

  const { report } = useBugHandler();

  const [savedWindGraphType] = useStorageSystem($WIND_GRAPH.default_graph, 'detailPanelWindGraphType', 'localStorage')
  
  const { upgrade, upgradeAvailable } = useUserUpgrades();
  const { domain, toggleDomain, resetDomain } = useUserHints();
  
  const showSaveContainerModal = ref(false);
  const dataBlockContent = ref(null);
  const windGraphDataField = ref(null);
  
  const isNCWind = computed(() => spotStore.isNCWind || false);
  const saveContainerDisplayName = computed(() => detailPanelStore.containerType?.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '));
  const containerSettings = computed(() => (containerType) => detailPanelStore.containerSettings ? detailPanelStore.containerSettings[containerType] : null);
  const showContentPopupMenu = computed(() => detailPanelStore.showContent && containers[detailPanelStore.containerType] && upgrade[3] !== -1);
  const spotId = computed(() => spotStore.spotId)

  // Need a container id based on either the favorite container id or spot id to be used as key for components.
  // This allows the components to rerender when switching between saved containers.
  const containerId = computed(() => detailPanelStore.favoriteContainerId || spotStore.spotId);

  const windGraphType = computed(() => {
    let type = savedWindGraphType.value || $WIND_GRAPH.default_graph;

    if (!detailPanelStore.containerSettings || !detailPanelStore.containerSettings['wind-graph'] || !detailPanelStore.containerSettings['wind-graph'].type) {
      return type;
    }

    return detailPanelStore.containerSettings['wind-graph']?.type === 'Advanced' ? 'detailed' : 'basic'
  });

  function changeTab(tab) {
    setContainerSettings(detailPanelStore.containerType);

    dataBlockContent.value = null;
    detailPanelStore.changeTab(tab);
  }

  function handleSaveContainerType() {
    showSaveContainerModal.value = true;
  }

  function getSettings(containerType) {
    if (!containers[containerType]) return {};

    return containers[containerType].getSettings();
  }

  function setContainerSettings(containerType) {
    if (!containers[containerType]) return;

    if (!detailPanelStore.containerSettings) detailPanelStore.containerSettings = {};

    const settings = getSettings(containerType);
    
    if (!settings || Object.keys(settings).length === 0) return;

    if (containerType === 'wind-graph') {
      // Remove selectedModelIds from settings to avoid conflicts with the graph's own selected model storage (localStorage)
      delete settings.selectedModelIds;
    }

    detailPanelStore.containerSettings[containerType] = settings;

    if (containerType === 'wind-graph') {
      const graphType = settings.type === 'Advanced' ? 'detailed' : 'basic';
      savedWindGraphType.value = graphType;
    }
  }

  function handleDetailPanelClose() {
    detailPanelStore.close();
    resetDomain();
  }

  provide('isNCWind', isNCWind.value);

  watchEffect(() => {
    detailPanelStore.setPanelElement(detailPanel.value);
  });

  window.$EventBus.addEventListener('cosmos-load-detail-panel', async(e) => {
    const { spot, tab } = e.detail;
    
    const spotInfo = {
      id: null,
      lat: null,
      lon: null,
      requestData: e.detail.requestData || false,
    }

    if (typeof spot === 'number' || typeof spot === 'string') {
      spotInfo.id = spot;
      spotInfo.requestData = true;
    } else if (typeof spot === 'object') {
      Object.assign(spotInfo, spot);
    } else {
      spotInfo.requestData = false;
    }
    
    if (spotInfo.id || (spotInfo.lat && spotInfo.lon)) {
      await detailPanelStore.loadSpot(spotInfo);
    }

    // Only open the panel if there is a spot to show or if the requested tab is the onsite report tab, which can be opened without a spot.
    if (spot || tab === 4) {      
      detailPanelStore.open(tab)
    }; 
  });

  window.$EventBus.addEventListener('cosmos-map-marker-clicked', async(e) => {
    const { spot } = e.detail;

    spot.requestData = false;
    
    await detailPanelStore.loadSpot(spot);
  });

  window.$EventBus.addEventListener('cosmos-onsite-report-clicked', async(e) => {
    const { marker } = e.detail;
    
    await detailPanelStore.loadSpot({
      lat: marker.settings.lat,
      lon: marker.settings.lon,
      requestData: true,
    });

    detailPanelStore.changeTab(4);
  });

  window.$EventBus.addEventListener('cosmos-wlmarker-clicked', async(e) => {
    const { spotId, lat, lon } = e.detail;
    
    await detailPanelStore.loadSpot({
      spot_id: spotId,
      lat,
      lon,
      requestData: true,
    });

    savedWindGraphType.value = 'detailed'
    windGraphDataField.value = 'waterlevel'
    detailPanelStore.open(1);
  });

  onErrorCaptured((e) => {
    const reportMeta = { 
      component: 'MapDetailPanel',
      containerType: detailPanelStore.containerType,
    };

    if (spotStore.spot) {
      reportMeta.spotId = spotStore.spotId;
      reportMeta.spotName = spotStore.spotName;
      reportMeta.lat = spotStore.lat;
      reportMeta.lon = spotStore.lon;
    }

    report(e, reportMeta);    

    return false;
  })    
</script>

<template>
  <Transition name="panel-slide">
    <div ref="detail-panel" v-if="detailPanelStore.panelOpen" class="jwx-map__right-panel jwx-map-detail-panel">
      <MapDetailPanelSpotHeader
        v-if="spotStore.spot?.name"
        @close="handleDetailPanelClose"
      />
      <aside class="jwx-map-detail-panel__sidebar">
        <div class="jwx-map-detail-panel__sidebar-buttons">
          <div v-if="detailPanelStore.showWindGraph" class="jwx-map-detail-panel__sidebar-button">
            <ControlButton
              v-if="detailPanelStore.showWindGraph"
              @click.prevent="changeTab(1)"
              :class="detailPanelStore.containerType === 'wind-graph' ? 'selected' : ''"
              data-transparent="true"
            >
              <Icon name="markers" class="jwx-button-control__icon" />
              <UserHint position="left" :key="`${spotStore.spotName}-1`" hintDomain="detail-panel">Wind Graphs</UserHint>
            </ControlButton>
          </div>
          <div v-if="detailPanelStore.showForecastTable" class="jwx-map-detail-panel__sidebar-button">
            <ControlButton
              @click.prevent="changeTab(2)"
              :class="detailPanelStore.containerType === 'forecast-table' ? 'selected' : ''"
            data-transparent="true"
            >
              <Icon name="binoculars" class="jwx-button-control__icon" />
              <UserHint position="left" :key="`${spotStore.spotName}-2`" hintDomain="detail-panel">Forecast Table</UserHint>
            </ControlButton>
          </div>
          <div v-if="detailPanelStore.showProForecast" class="jwx-map-detail-panel__sidebar-button">
            <ControlButton
              @click.prevent="changeTab(3)"
              :class="detailPanelStore.containerType === 'pro-forecast' ? 'selected' : ''"
              data-transparent="true"
            >
              <Icon name="pro-forecast" class="jwx-button-control__icon" />
              <UserHint position="left" :key="`${spotStore.spotName}-3`" hintDomain="detail-panel">Pro Forecast</UserHint>
            </ControlButton>
          </div>
          <div v-if="detailPanelStore.showGorgeWindbow" class="jwx-map-detail-panel__sidebar-button">
            <ControlButton
              @click.prevent="changeTab(11)"
              :class="detailPanelStore.containerType === 'gorge-windbow' ? 'selected' : ''"
              data-transparent="true"
            >
              <Icon name="gorge-windbow" class="jwx-button-control__icon" />
              <UserHint position="left" :key="`${spotStore.spotName}-11`" hintDomain="detail-panel">Gorge Windbow</UserHint>
            </ControlButton>
          </div>
          <div v-if="detailPanelStore.showAdvisories" class="jwx-map-detail-panel__sidebar-button">
            <ControlButton
              @click.prevent="changeTab(10)"
              :class="detailPanelStore.containerType === 'advisories' ? 'selected' : ''"
              data-transparent="true"
            >
              <Icon name="advisories" class="jwx-button-control__icon" />
              <span v-if="alertStore.alertCount > 0" class="jwx-button-control__badge-count">{{ alertStore.alertCount }}</span>
              <UserHint position="left" :key="`${spotStore.spotName}-10`" hintDomain="detail-panel">Advisories</UserHint>
            </ControlButton>
          </div>
          <div v-if="detailPanelStore.showMarineForecasts" class="jwx-map-detail-panel__sidebar-button">
            <ControlButton
              @click.prevent="changeTab(6)"
              :class="detailPanelStore.containerType === 'marine-forecast' ? 'selected' : ''"
              data-transparent="true"
            >
              <Icon name="marine-fx" class="jwx-button-control__icon" />
              <UserHint position="left" :key="`${spotStore.spotName}-6`" hintDomain="detail-panel">Marine Forecasts</UserHint>
            </ControlButton>
          </div>
          <div v-if="detailPanelStore.showOnsiteReports" class="jwx-map-detail-panel__sidebar-button">
            <ControlButton
              @click.prevent="changeTab(4)"
              :class="detailPanelStore.containerType === 'onsite-report' ? 'selected' : ''"
              data-transparent="true"
            >
              <Icon name="onsite-reports" class="jwx-button-control__icon" />
              <UserHint position="left" :key="`${spotStore.spotName}-4`" hintDomain="detail-panel">Onsite Reports</UserHint>
            </ControlButton>
          </div>
          <div v-if="detailPanelStore.showNearbyTides" class="jwx-map-detail-panel__sidebar-button">
            <ControlButton
              @click.prevent="changeTab(5)"
              :class="detailPanelStore.containerType === 'nearby-tides' ? 'selected' : ''"
              data-transparent="true"
            >
              <Icon name="tides" class="jwx-button-control__icon" />
              <UserHint position="left" :key="`${spotStore.spotName}-5`" hintDomain="detail-panel">Nearby Tides</UserHint>
            </ControlButton>
          </div>
          <div v-if="detailPanelStore.showArchiveData" class="jwx-map-detail-panel__sidebar-button">
            <ControlButton
              @click.prevent="changeTab(7)"
              :class="detailPanelStore.containerType === 'archive-data' ? 'selected' : ''"
              data-transparent="true"
            >
              <Icon name="clock-rotate-left" class="jwx-button-control__icon" />
              <UserHint position="left" :key="`${spotStore.spotName}-7`" hintDomain="detail-panel">Archive Data</UserHint>
            </ControlButton>
          </div>
          <div v-if="detailPanelStore.showStatsData" class="jwx-map-detail-panel__sidebar-button">
            <ControlButton
              @click.prevent="changeTab(8)"
              :class="detailPanelStore.containerType === 'spot-stats' ? 'selected' : ''"
              data-transparent="true"
            >
              <Icon name="chart-bar" class="jwx-button-control__icon" />
              <UserHint position="left" :key="`${spotStore.spotName}-8`" hintDomain="detail-panel">Spot Stats</UserHint>
            </ControlButton>
          </div>
          <div v-if="detailPanelStore.showInfoData" class="jwx-map-detail-panel__sidebar-button">
            <ControlButton
              @click.prevent="changeTab(9)"
              :class="detailPanelStore.containerType === 'spot-info' ? 'selected' : ''"
              data-transparent="true"
            >
              <Icon name="info" class="jwx-button-control__icon" />
              <UserHint position="left" :key="`${spotStore.spotName}-9`" hintDomain="detail-panel">Spot Info</UserHint>
            </ControlButton>
          </div>
        </div>
        <ControlButton
          @click.prevent="toggleDomain('detail-panel')"
          data-surface="1"
          data-shape="circle"
          :data-selected="domain === 'detail-panel'"
        >
          <Icon name="question-mark" class="jwx-button-control__icon" />
        </ControlButton>
      </aside>

      <main v-if="detailPanelStore.showContent" class="jwx-map-detail-panel__content">
        <div v-if="detailPanelStore.containerType === 'wind-graph'" class="jwx-map-detail-panel__content-container">
          <div ref="wind-graphs-toolbar" v-if="!upgrade[0]" class="jwx-map-detail-panel__toolbar"></div>
          <div class="jwx-map-detail-panel__split-content jwx-flex-1">
            <div ref="wind-graphs-sidebar" class="jwx-map-detail-panel__data-column"></div>
            <div class="jwx-map-detail-panel__main-column" style="overflow-x: hidden;">
              <WindGraphsContainer
                ref="wind-graph-container"
                @containerSettings:change="setContainerSettings('wind-graph')"
                :containerSettings="containerSettings('wind-graph')"
                :showCurrentConditions="true"
                :showLastUpdateTimestamp="true"
                :controlsTarget="windGraphsToolbar"
                :sidebarTarget="windGraphsSidebar"
                :windGraphType="windGraphType"
                :windGraphDataField="windGraphDataField"
                :upgrade="upgrade"
                :key="containerId"
                class="jwx-page-block--padding-sm"
              />
            </div>
          </div>
        </div>
        <div v-if="detailPanelStore.containerType === 'forecast-table'" class="jwx-map-detail-panel__content-container">
          <ForecastTableContainer 
            ref="forecast-table-container"
            @containerSettings:change="setContainerSettings('forecast-table')"
            :key="containerId"
            :containerSettings="containerSettings('forecast-table')"
            data-condensed="true"
          />
        </div>
        <div v-if="detailPanelStore.containerType === 'pro-forecast'"  class="jwx-pro-forecast" :class="{'jwx-h-100': upgradeAvailable}">
          <ProForecastContainer 
            ref="pro-forecast-container"
            :key="containerId"
            :memberLevel="userStore.memberLevel"
            :collapsible="false"
            :containerSettings="detailPanelStore.containerSettings"
            minHeight="100%"
          />
        </div>
        <div v-if="detailPanelStore.containerType === 'advisories'" class="jwx-page-block jwx-data-container" data-content="advisories" data-condensed="true" style="height: 100%;">
          <AdvisoriesListing :key="spotStore.spotId" />
        </div>
        <div v-if="detailPanelStore.containerType === 'marine-forecast'" class="jwx-page-block jwx-data-container" data-content="advisories" data-condensed="true" style="height: 100%;">
          <MarineForecastListing />
        </div>
        <div v-if="detailPanelStore.containerType === 'onsite-report'" class="jwx-map-detail-panel__content-container">
          <div class="jwx-page-block jwx-data-container jwx-flex-1" data-content="onsite-reports" data-condensed="true" style="height: 100%;">
            <OnsiteReportsContent ref="onsite-report-container" />
          </div>
        </div>
        <div v-if="detailPanelStore.containerType === 'nearby-tides'" class="jwx-map-detail-panel__content-container">
          <div class="jwx-page-block jwx-data-container jwx-flex-1" data-content="tides" data-condensed="true" style="height: 100%;">
            <NearbyTidesContent 
              ref="nearby-tides-container"
              :graphHeight="300"
              :spotId="spotStore.spotId"
            />
          </div>
        </div>
        <div v-if="detailPanelStore.containerType === 'archive-data'" class="jwx-map-detail-panel__content-container">
          <div  class="jwx-page-block jwx-data-container jwx-flex-1" data-content="archive-data" data-condensed="true" style="height: 100%;">
            <ArchiveDataContent ref="archive-data-container" />
          </div>
        </div>
        <div v-if="detailPanelStore.containerType === 'spot-stats'" class="jwx-map-detail-panel__content-container">
          <div class="jwx-page-block jwx-data-container jwx-flex-1" data-content="spot-stats" data-condensed="true" style="height: 100%;">
            <StatsContent :spotId="spotId" />
          </div>
        </div>
        <div v-if="detailPanelStore.containerType === 'spot-info'" class="jwx-map-detail-panel__content-container">
          <StationInfoContent v-if="detailPanelStore.containerType === 'spot-info'"/>
        </div>
        <div v-if="detailPanelStore.containerType === 'gorge-windbow'" class="jwx-map-detail-panel__content-container">
          <GorgeWindbowContainer 
            ref="gorge-windbow-container"
            v-if="detailPanelStore.containerType === 'gorge-windbow'" 
            :key="containerId"
            :upgrade="upgrade"
            data-condensed="true"
          />
        </div>
      </main>
      <Transition name="slide-up-fade">
        <div v-if="showContentPopupMenu" class="jwx-map-detail-panel__content-popup-wrapper">
          <Popup
            :teleport="true"
            :positionY="'top'"
            :positionX="'right'"
            :id="`detail-panel-popup-menu-${spotStore.spotId}`"
          >
            <template #button-content>
              <div class="jwx-button-control jwx-shadow" data-shape="circle">
                <Icon name="ellipsis-vertical" class="jwx-button-control__icon" />
                <span class='sr-only'>Open Detail Panel Actions Menu</span>
              </div>
            </template>
            <template #menu-content>
              <div class="jwx-popup-menu">
                <div class="jwx-action-menu">
                  <ul class="jwx-action-menu__list">
                    <li v-if="userStore.memberLevel > 0" class="jwx-action-menu__action">
                      <button 
                        type="button"
                        @click.prevent="handleSaveContainerType"
                        class="jwx-action-menu__action-button"
                      >
                        <Icon name="star" class="jwx-action-menu__action-icon" />
                        <span class=jwx-action-menu__action-label>Save {{ saveContainerDisplayName }}</span>
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </template>
          </Popup>    
        </div>
      </Transition>

    </div>
  </Transition>

  <ModalSaveContainerToProfile
    v-if="showSaveContainerModal"
    v-model:showModal="showSaveContainerModal"
    :containerTypeId="detailPanelStore.savedContainerType"
    :containerTypeName="saveContainerDisplayName"
    :settings="getSettings(detailPanelStore.containerType)"
  />
</template>